import { useState, useEffect, type Key } from 'react'
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Input,
  Select,
  Form,
  message,
  Tabs,
  Tooltip,
  Popconfirm,
  Statistic,
  Row,
  Col,
  Typography,
} from 'antd'
import type { TableProps } from 'antd'
import {
  ReloadOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  UserSwitchOutlined,
  FileTextOutlined,
  InboxOutlined,
  SwapOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { crmApi, authApi } from '../api/client'

const { TextArea } = Input
const { Text } = Typography

// CRM 冲突状态配置
const conflictStatusConfig: Record<string, { color: string; text: string }> = {
  conflict_detected: { color: 'red', text: '冲突待处理' },
  conflict_resolved: { color: 'green', text: '已解决' },
  pending_check: { color: 'orange', text: '检测中' },
  check_failed: { color: 'default', text: '检测失败' },
  none: { color: 'default', text: '无冲突' },
}

interface ConflictProject {
  project_id: string
  project_name?: string
  twitter_handle?: string
  user_id: string
  bb_owner_email?: string
  crm_conflict_status: string
  crm_conflict_detected_at?: string
  crm_conflict_details?: Record<string, unknown>
  crm_lead_id?: string
  crm_assigned_bd_email?: string
  crm_synced_at?: string
}

interface ScanReport {
  id: string
  scan_date: string
  triggered_by?: string
  total_projects_scanned: number
  conflicts_detected: number
  new_conflicts: number
  conflicts_resolved: number
  status: string
  error_message?: string
  duration_seconds?: number
  created_at: string
}

// ==================== 冲突项目 Tab ====================

function ConflictProjectsTab() {
  const [data, setData] = useState<ConflictProject[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [resolveModalOpen, setResolveModalOpen] = useState(false)
  const [resolveTarget, setResolveTarget] = useState<ConflictProject | null>(null)
  const [resolveNotes, setResolveNotes] = useState('')
  const [resolving, setResolving] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
  const [transferModalOpen, setTransferModalOpen] = useState(false)
  const [transferTarget, setTransferTarget] = useState<ConflictProject | null>(null)
  const [transferBatch, setTransferBatch] = useState(false)
  const [transferring, setTransferring] = useState(false)
  const [twitterFilter, setTwitterFilter] = useState('')
  const [ownerFilter, setOwnerFilter] = useState('')
  const limit = 20

  const fetchData = async () => {
    setLoading(true)
    try {
      const result = await crmApi.getConflicts({
        page,
        limit,
        twitter_handle: twitterFilter || undefined,
        bb_owner: ownerFilter || undefined,
      })
      setData(result.items || [])
      setTotal(result.total || 0)
    } catch (err: any) {
      message.error(`获取数据失败: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [page, twitterFilter, ownerFilter])

  const handleResolve = async () => {
    if (!resolveTarget) return
    setResolving(true)
    try {
      await crmApi.resolveConflict(resolveTarget.project_id, resolveNotes)
      message.success('冲突已标记为已解决')
      setResolveModalOpen(false)
      setResolveNotes('')
      setResolveTarget(null)
      fetchData()
    } catch (err: any) {
      message.error(`操作失败: ${err.message}`)
    } finally {
      setResolving(false)
    }
  }

  const handleTransfer = async () => {
    const ids = transferBatch
      ? selectedRowKeys.map(String)
      : transferTarget
        ? [transferTarget.project_id]
        : []
    if (ids.length === 0) return
    setTransferring(true)
    try {
      const res = await crmApi.transferConflicts(ids)
      message.success(`转移成功 ${res.transferred} 个 / 失败 ${res.failed} 个`)
      setTransferModalOpen(false)
      setTransferTarget(null)
      setTransferBatch(false)
      setSelectedRowKeys([])
      fetchData()
    } catch (err: any) {
      message.error(`操作失败: ${err.message}`)
    } finally {
      setTransferring(false)
    }
  }

  const columns: TableProps<ConflictProject>['columns'] = [
    {
      title: '项目名称',
      dataIndex: 'project_name',
      key: 'project_name',
      render: (name: string, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name || '-'}</Text>
          {record.twitter_handle && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              @{record.twitter_handle}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'BB 当前归属',
      key: 'bb_owner',
      render: (_: unknown, record: ConflictProject) => {
        // 优先后端现算的「当前归属」(user_id→email),兜底冲突检测快照
        const raw =
          record.bb_owner_email ||
          (record.crm_conflict_details as Record<string, unknown> | undefined)?.bb_owner_email
        const owner = typeof raw === 'string' ? raw : undefined
        return owner ? <Text>{owner}</Text> : <Text type="secondary">-</Text>
      },
    },
    {
      title: 'CRM 指派 BD',
      dataIndex: 'crm_assigned_bd_email',
      key: 'crm_assigned_bd_email',
      render: (email: string) => email || '-',
    },
    {
      title: '冲突状态',
      dataIndex: 'crm_conflict_status',
      key: 'crm_conflict_status',
      width: 120,
      render: (status: string) => {
        const cfg = conflictStatusConfig[status] || { color: 'default', text: status }
        return <Tag color={cfg.color}>{cfg.text}</Tag>
      },
    },
    {
      title: 'CRM Lead ID',
      dataIndex: 'crm_lead_id',
      key: 'crm_lead_id',
      render: (id: string) => id || '-',
    },
    {
      title: '检测时间',
      dataIndex: 'crm_conflict_detected_at',
      key: 'crm_conflict_detected_at',
      width: 140,
      render: (time: string) =>
        time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: unknown, record: ConflictProject) => {
        const canTransfer =
          record.crm_conflict_status === 'conflict_detected' &&
          !!record.crm_assigned_bd_email
        return (
          <Space size="small" wrap>
            {canTransfer && (
              <Button
                type="primary"
                size="small"
                icon={<SwapOutlined />}
                onClick={() => {
                  setTransferTarget(record)
                  setTransferBatch(false)
                  setTransferModalOpen(true)
                }}
              >
                转移归属
              </Button>
            )}
            {record.crm_conflict_status === 'conflict_detected' && (
              <Button
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  setResolveTarget(record)
                  setResolveModalOpen(true)
                }}
              >
                解决冲突
              </Button>
            )}
          </Space>
        )
      },
    },
  ]

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="按 Twitter Handle 过滤（如 pharos）"
          style={{ width: 280 }}
          onSearch={(v) => { setTwitterFilter(v.trim()); setPage(1) }}
          onChange={(e) => { if (!e.target.value) { setTwitterFilter(''); setPage(1) } }}
          allowClear
        />
        <Input.Search
          placeholder="按 BB 归属人过滤（如 luke）"
          style={{ width: 260 }}
          onSearch={(v) => { setOwnerFilter(v.trim()); setPage(1) }}
          onChange={(e) => { if (!e.target.value) { setOwnerFilter(''); setPage(1) } }}
          allowClear
        />
        <Button icon={<ReloadOutlined />} onClick={fetchData}>
          刷新
        </Button>
        <Button
          type="primary"
          icon={<SwapOutlined />}
          disabled={selectedRowKeys.length === 0}
          onClick={() => {
            setTransferBatch(true)
            setTransferTarget(null)
            setTransferModalOpen(true)
          }}
        >
          批量转移归属 ({selectedRowKeys.length})
        </Button>
      </Space>

      <Table
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as Key[]),
          getCheckboxProps: (r: ConflictProject) => ({
            disabled: r.crm_conflict_status !== 'conflict_detected',
          }),
        }}
        columns={columns}
        dataSource={data}
        rowKey="project_id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          onChange: (p) => setPage(p),
        }}
      />

      <Modal
        title="转移归属"
        open={transferModalOpen}
        onOk={handleTransfer}
        onCancel={() => {
          setTransferModalOpen(false)
          setTransferTarget(null)
          setTransferBatch(false)
        }}
        confirmLoading={transferring}
        okText="确认转移"
        okButtonProps={{ danger: true }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {transferBatch ? (
            <Text>
              将把选中的 <Text strong>{selectedRowKeys.length}</Text> 个冲突项目，
              各自转移给其 CRM 指派 BD（目标 BD 不在 BB 中时会跳过并提示）。
            </Text>
          ) : (
            <Text>
              将项目 <Text strong>{transferTarget?.project_name}</Text> 的归属，
              转移给 CRM 指派 BD：<Text strong>{transferTarget?.crm_assigned_bd_email}</Text>
            </Text>
          )}
          <Text type="warning">此操作会真正修改项目归属人，不可撤销。</Text>
        </Space>
      </Modal>

      <Modal
        title="解决冲突"
        open={resolveModalOpen}
        onOk={handleResolve}
        onCancel={() => {
          setResolveModalOpen(false)
          setResolveNotes('')
          setResolveTarget(null)
        }}
        confirmLoading={resolving}
        okText="确认解决"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            确认将项目 <Text strong>{resolveTarget?.project_name}</Text> 的冲突标记为已解决？
          </Text>
          <TextArea
            rows={3}
            placeholder="填写解决备注（可选）"
            value={resolveNotes}
            onChange={(e) => setResolveNotes(e.target.value)}
          />
        </Space>
      </Modal>
    </>
  )
}

// ==================== 扫描报告 Tab ====================

function ScanReportsTab() {
  const [reports, setReports] = useState<ScanReport[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [triggering, setTriggering] = useState(false)
  const limit = 20

  const fetchReports = async () => {
    setLoading(true)
    try {
      const result = await crmApi.getScanReports({ page, limit })
      setReports(result.items || [])
      setTotal(result.total || 0)
    } catch (err: any) {
      message.error(`获取报告失败: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchReports() }, [page])

  const handleTriggerScan = async () => {
    setTriggering(true)
    try {
      await crmApi.triggerScan()
      message.success('扫描任务已启动，请稍后刷新查看结果')
    } catch (err: any) {
      message.error(`触发失败: ${err.message}`)
    } finally {
      setTriggering(false)
    }
  }

  const latestReport = reports[0]

  const columns: TableProps<ScanReport>['columns'] = [
    {
      title: '扫描时间',
      dataIndex: 'scan_date',
      key: 'scan_date',
      width: 160,
      render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '触发方式',
      dataIndex: 'triggered_by',
      key: 'triggered_by',
      width: 100,
      render: (by: string) => (
        <Tag color={by === 'auto' ? 'blue' : 'purple'}>
          {by === 'auto' ? '自动' : '手动'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: string) => {
        const map: Record<string, { color: string; text: string }> = {
          completed: { color: 'green', text: '完成' },
          running: { color: 'blue', text: '运行中' },
          failed: { color: 'red', text: '失败' },
        }
        const cfg = map[status] || { color: 'default', text: status }
        return <Tag color={cfg.color}>{cfg.text}</Tag>
      },
    },
    {
      title: '扫描项目',
      dataIndex: 'total_projects_scanned',
      key: 'total_projects_scanned',
      width: 90,
      render: (n: number) => n ?? '-',
    },
    {
      title: '发现冲突',
      dataIndex: 'conflicts_detected',
      key: 'conflicts_detected',
      width: 90,
      render: (n: number) => (
        <Text type={n > 0 ? 'danger' : undefined}>{n ?? '-'}</Text>
      ),
    },
    {
      title: '新增冲突',
      dataIndex: 'new_conflicts',
      key: 'new_conflicts',
      width: 90,
      render: (n: number) => (
        <Text type={n > 0 ? 'warning' : undefined}>{n ?? '-'}</Text>
      ),
    },
    {
      title: '耗时(s)',
      dataIndex: 'duration_seconds',
      key: 'duration_seconds',
      width: 80,
      render: (d: number) => (d != null ? d.toFixed(1) : '-'),
    },
    {
      title: '错误信息',
      dataIndex: 'error_message',
      key: 'error_message',
      render: (msg: string) =>
        msg ? (
          <Tooltip title={msg}>
            <Text type="danger" style={{ fontSize: 12 }}>
              {msg.length > 30 ? `${msg.slice(0, 30)}...` : msg}
            </Text>
          </Tooltip>
        ) : null,
    },
  ]

  return (
    <>
      {latestReport && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="最近扫描项目数"
                value={latestReport.total_projects_scanned}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="发现冲突"
                value={latestReport.conflicts_detected}
                valueStyle={{ color: latestReport.conflicts_detected > 0 ? '#f5222d' : undefined }}
                prefix={<WarningOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="新增冲突"
                value={latestReport.new_conflicts}
                valueStyle={{ color: latestReport.new_conflicts > 0 ? '#fa8c16' : undefined }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="扫描耗时"
                value={latestReport.duration_seconds?.toFixed(1) ?? '-'}
                suffix="s"
              />
            </Card>
          </Col>
        </Row>
      )}

      <Space style={{ marginBottom: 16 }}>
        <Popconfirm
          title="确认触发扫描？"
          description="将对所有项目执行 CRM 冲突检测，可能需要数分钟。"
          onConfirm={handleTriggerScan}
        >
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={triggering}
          >
            立即扫描
          </Button>
        </Popconfirm>
        <Button icon={<ReloadOutlined />} onClick={fetchReports}>
          刷新
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={reports}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          onChange: (p) => setPage(p),
        }}
      />
    </>
  )
}

// ==================== 公海池 Tab ====================

function PublicPoolTab() {
  const [data, setData] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const limit = 20

  const fetchData = async () => {
    setLoading(true)
    try {
      const result = await crmApi.getPublicPool({ page, limit })
      setData(result.items || [])
      setTotal(result.total || 0)
    } catch {
      message.error('加载公海池失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [page])

  const handleClaim = async (projectId: string) => {
    try {
      const result = await crmApi.claimPublicPoolProject(projectId)
      message.success(`项目已认领: ${result.claimed_by}`)
      fetchData()
    } catch {
      message.error('认领失败')
    }
  }

  const columns = [
    { title: '项目名称', dataIndex: 'project_name', key: 'name' },
    { title: 'Twitter', dataIndex: 'twitter_handle', key: 'twitter', render: (h: string) => h ? `@${h}` : '-' },
    { title: '网站', dataIndex: 'website', key: 'website' },
    { title: '进池原因', dataIndex: 'public_pool_reason', key: 'reason',
      render: (r: string) => <Tag>{r || '-'}</Tag> },
    { title: 'CRM Lead', dataIndex: 'crm_lead_id', key: 'crm_lead' },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at',
      render: (d: string) => d ? dayjs(d).format('MM-DD HH:mm') : '-' },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: any) => (
        <Popconfirm title="确认认领此项目？" onConfirm={() => handleClaim(record.project_id)}>
          <Button type="primary" size="small">认领</Button>
        </Popconfirm>
      ),
    },
  ]

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ReloadOutlined />} onClick={fetchData}>刷新</Button>
      </Space>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="project_id"
        loading={loading}
        pagination={{ current: page, pageSize: limit, total, onChange: (p) => setPage(p) }}
      />
    </>
  )
}

// ==================== Admin 账号管理 Tab ====================

function AdminAccountsTab() {
  const [data, setData] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [rebindModal, setRebindModal] = useState(false)
  const [rebindTarget, setRebindTarget] = useState<any>(null)
  const [bdUsers, setBdUsers] = useState<any[]>([])
  const [rebindForm] = Form.useForm()
  const limit = 50

  const fetchData = async () => {
    setLoading(true)
    try {
      const result = await crmApi.adminListAccounts({ page, limit })
      setData(result.items || [])
      setTotal(result.total || 0)
    } catch {
      message.error('加载账号列表失败')
    } finally {
      setLoading(false)
    }
  }

  const fetchBdUsers = async () => {
    try {
      const users = await authApi.getBdUsers()
      setBdUsers(users)
    } catch { /* ignore */ }
  }

  useEffect(() => { fetchData(); fetchBdUsers() }, [page])

  const handleRebind = async () => {
    try {
      const values = await rebindForm.validateFields()
      await crmApi.forceRebindAccount(rebindTarget.id, values.new_user_id, values.reason)
      message.success('账号已重绑')
      setRebindModal(false)
      rebindForm.resetFields()
      fetchData()
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '重绑失败')
    }
  }

  const columns = [
    { title: '账号名', dataIndex: 'account_name', key: 'name' },
    { title: '类型', dataIndex: 'account_type', key: 'type',
      render: (t: string) => <Tag color="blue">{t}</Tag> },
    { title: '健康', dataIndex: 'health', key: 'health',
      render: (h: string) => <Tag color={h === 'healthy' ? 'green' : 'orange'}>{h}</Tag> },
    { title: '所属BD', key: 'owner',
      render: (_: any, r: any) => (
        <span>{r.owner_name} <span style={{ color: '#888', fontSize: 12 }}>({r.owner_email})</span></span>
      ) },
    { title: 'BD状态', dataIndex: 'owner_status', key: 'owner_status',
      render: (s: string) => <Tag color={s === 'active' ? 'green' : 'default'}>{s}</Tag> },
    { title: '今日使用', dataIndex: 'daily_usage_count', key: 'daily' },
    { title: '总使用', dataIndex: 'total_usage_count', key: 'total' },
    {
      title: '操作', key: 'actions',
      render: (_: any, record: any) => (
        <Button
          size="small"
          icon={<UserSwitchOutlined />}
          onClick={() => { setRebindTarget(record); setRebindModal(true) }}
        >
          重绑
        </Button>
      ),
    },
  ]

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ReloadOutlined />} onClick={fetchData}>刷新</Button>
      </Space>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{ current: page, pageSize: limit, total, onChange: (p) => setPage(p) }}
      />
      <Modal
        title={`强制重绑账号 - ${rebindTarget?.account_name}`}
        open={rebindModal}
        onOk={handleRebind}
        onCancel={() => { setRebindModal(false); rebindForm.resetFields() }}
        okText="确认重绑"
        okButtonProps={{ danger: true }}
      >
        <Form form={rebindForm} layout="vertical">
          <Form.Item name="new_user_id" label="绑定到 BD" rules={[{ required: true, message: '请选择目标BD' }]}>
            <Select placeholder="选择 BD 用户">
              {bdUsers.map((u) => (
                <Select.Option key={u.id} value={u.id}>{u.name} ({u.email})</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="reason" label="重绑理由" rules={[{ required: true, message: '请填写理由' }]}>
            <Input.TextArea rows={2} placeholder="必填，将记录到审计日志" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

// ==================== 审计日志 Tab ====================

function AuditLogsTab() {
  const [data, setData] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [actionFilter, setActionFilter] = useState('')
  const limit = 50

  const fetchData = async () => {
    setLoading(true)
    try {
      const result = await crmApi.getAuditLogs({
        action: actionFilter || undefined,
        page,
        limit,
      })
      setData(result.items || [])
      setTotal(result.total || 0)
    } catch {
      message.error('加载审计日志失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [page, actionFilter])

  const columns = [
    { title: '时间', dataIndex: 'created_at', key: 'created_at', width: 140,
      render: (d: string) => dayjs(d).format('MM-DD HH:mm:ss') },
    { title: '操作', dataIndex: 'action', key: 'action', width: 160,
      render: (a: string) => <Tag color="blue">{a}</Tag> },
    { title: '资源类型', dataIndex: 'resource_type', key: 'resource_type', width: 100 },
    { title: '资源ID', dataIndex: 'resource_id', key: 'resource_id', width: 120,
      render: (id: string) => id ? <span style={{ fontSize: 11 }}>{id.slice(0, 8)}...</span> : '-' },
    { title: '操作人', dataIndex: 'user_id', key: 'user_id', width: 120,
      render: (id: string) => id ? <span style={{ fontSize: 11 }}>{id.slice(0, 8)}...</span> : '系统' },
    { title: '变更内容', key: 'change',
      render: (_: any, r: any) => (
        <Space size={4} direction="vertical" style={{ fontSize: 12 }}>
          {r.old_value && <span style={{ color: '#f5222d' }}>旧: {JSON.stringify(r.old_value)}</span>}
          {r.new_value && <span style={{ color: '#52c41a' }}>新: {JSON.stringify(r.new_value)}</span>}
        </Space>
      ) },
  ]

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="按操作类型搜索（如 account.force_rebind）"
          style={{ width: 300 }}
          onSearch={(v) => { setActionFilter(v); setPage(1) }}
          allowClear
        />
        <Button icon={<ReloadOutlined />} onClick={fetchData}>刷新</Button>
      </Space>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{ current: page, pageSize: limit, total, onChange: (p) => setPage(p) }}
        size="small"
      />
    </>
  )
}

// ==================== Main Component ====================

export default function ConflictCenter() {
  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
        <WarningOutlined style={{ fontSize: 20, color: '#f5222d' }} />
        <h2 style={{ margin: 0 }}>CRM 冲突管理</h2>
      </div>

      <Tabs
        defaultActiveKey="conflicts"
        items={[
          {
            key: 'conflicts',
            label: '冲突项目',
            children: <ConflictProjectsTab />,
          },
          {
            key: 'reports',
            label: '扫描报告',
            children: <ScanReportsTab />,
          },
          {
            key: 'public-pool',
            label: <span><InboxOutlined /> 公海池</span>,
            children: <PublicPoolTab />,
          },
          {
            key: 'admin-accounts',
            label: <span><UserSwitchOutlined /> 账号管理</span>,
            children: <AdminAccountsTab />,
          },
          {
            key: 'audit-logs',
            label: <span><FileTextOutlined /> 审计日志</span>,
            children: <AuditLogsTab />,
          },
        ]}
      />
    </div>
  )
}
